import { PLAYER_H, WORLD_H, type LevelDef, type Platform, type Point, type SwitchDef } from './types';

/** Zero-based campaign order. Each call to getLevel returns fresh, mutable geometry. */
export const LEVEL_COUNT = 8;
export const LEVEL_META: Pick<LevelDef, 'id' | 'title' | 'subtitle' | 'mechanic' | 'theme' | 'par'>[] = [
  { id: 0, title: 'First Light', subtitle: 'A welcome made together', mechanic: 'Gather on the welcome plate, then boost a scout onto the trunk', theme: 'grove', par: 40 },
  { id: 1, title: 'A Little Higher', subtitle: 'A shoulder becomes a path', mechanic: 'Stack two, then unfold the bough walk', theme: 'grove', par: 55 },
  { id: 2, title: 'Leave a Light On', subtitle: 'Nobody is left at the door', mechanic: 'Hold the lower door; open an upper return route', theme: 'ruins', par: 65 },
  { id: 3, title: 'Skipstone Signal', subtitle: 'Hold the stone; cross together', mechanic: 'Half hold a rising stone while the rest cross and signal the bridge', theme: 'brook', par: 70 },
  { id: 4, title: 'Moonwake Ferry', subtitle: 'There is time for another crossing', mechanic: 'Ride a moving ferry and assemble on the far bank', theme: 'brook', par: 100 },
  { id: 5, title: 'Cargo Company', subtitle: 'Let the parcel take your place', mechanic: 'Boost a pusher over the wall; the parked parcel opens the way back', theme: 'ruins', par: 75 },
  { id: 6, title: 'Hushlight Crossing', subtitle: 'Hold for friends; wait for quiet', mechanic: 'Protect the first crossing, then use the dark interval', theme: 'night', par: 70 },
  { id: 7, title: 'All Aglow', subtitle: 'Every small kindness carries on', mechanic: 'Raise a tall tower, gather in pairs, and park cargo to light the final bridge', theme: 'night', par: 110 },
];

const FLOOR = 555;
const quorum = (size: number) => Math.max(2, Math.ceil(size / 2));
const keepers = (size: number) => Math.max(1, Math.floor(size / 2));
const spawns = (size: number, start = 48, stride = 44): Point[] =>
  Array.from({ length: size }, (_, i) => ({ x: start + stride * i, y: FLOOR - PLAYER_H }));

const ground = (id: string, x: number, w: number, y = FLOOR): Platform =>
  ({ id, x, y, w, h: WORLD_H - y, kind: 'ground' });
const ledge = (id: string, x: number, y: number, w: number, h = 16): Platform =>
  ({ id, x, y, w, h, kind: 'wood' });
// A closed door plus its lintel fills the entire vertical cut. A solo jump cannot
// bypass a door by landing on its top, including when a step is nearby.
const lintel = (id: string, x: number, y = 335, w = 24): Platform =>
  ({ id: `${id}-lintel`, x, y: 0, w, h: y, kind: 'stone' });
const plate = (
  id: string, label: string, x: number, required: number, mode: SwitchDef['mode'] = 'latch',
  y = FLOOR, w = required * 44 + 24, grace = 0,
): SwitchDef => ({ id, label, x, y: y - 10, w, h: 10, required, mode, grace });
// An all-hands latch: one 36px slot per courier, anchored on its RIGHT edge so
// the strip grows toward the spawns as the party grows. Disconnects shrink the
// effective requirement (engine cap); the geometry never changes mid-attempt.
const GATHER_STRIDE = 36;
export const gatherWidth = (size: number) => GATHER_STRIDE * size + 16;
export const gatherSlots = (rightEdge: number, size: number) =>
  Array.from({ length: size }, (_, i) => rightEdge - gatherWidth(size) + 8 + GATHER_STRIDE * i);
const gather = (id: string, label: string, rightEdge: number, size: number): SwitchDef =>
  plate(id, label, rightEdge - gatherWidth(size), size, 'latch', FLOOR, gatherWidth(size));

type Layout = Pick<LevelDef, 'hint' | 'platforms' | 'switches' | 'gates' | 'hazards' | 'keys' | 'crates' | 'exit' | 'signs'>;
const layouts: ((size: number) => Layout)[] = [
  // 1: A forgiving, irreversible first success. Every connected courier stands
  // on the welcome strip before the door opens; the latch then frees them all.
  // Beyond the door a 110 px hollow trunk holds the ember: one friend boosts a
  // scout onto it, and the scout's landing lights the trunk, opening the tunnel
  // beneath it for everybody else. The key is AFTER the team door, so that door
  // must not require it.
  size => ({
    hint: 'Stand together on the welcome plate. Then give a friend a shoulder up onto the trunk: its light opens the tunnel home for everyone.',
    platforms: [
      ground('meadow', 0, 1200), lintel('welcome-door', 704),
      // The trunk cap is walkable; the hollow beneath it is a tunnel gate the whole way through.
      { id: 'lantern-stump', x: 824, y: 445, w: 104, h: 62, kind: 'ground' },
    ],
    switches: [gather('welcome', 'Every light', 696, size), plate('stump', 'Light the trunk', 832, 1, 'latch', 445, 88)],
    gates: [
      { id: 'welcome-door', x: 704, y: 335, w: 24, h: 220, switchIds: ['welcome'] },
      { id: 'stump-tunnel', x: 824, y: 507, w: 104, h: 48, switchIds: ['stump'] },
    ],
    hazards: [],
    keys: [{ id: 'first-ember', x: 844, y: 397, w: 24, h: 24 }],
    crates: [],
    exit: { x: 1044, y: 443, w: 148, h: 112 },
    signs: [
      { x: 470, y: 502, text: 'EVERY LIGHT · stays lit', icon: 'friends' },
      { x: 760, y: 470, text: 'A shoulder, then a hop', icon: 'stack' },
      { x: 876, y: 410, text: 'Light it · tunnel opens', icon: 'switch' },
      { x: 1050, y: 414, text: 'Everyone home', icon: 'home' },
    ],
  }),
  // 2: A pair retains the original boost-and-unfold lesson. Larger parties split
  // into a scout, an anchor, and two span crews. All temporary jobs happen before
  // the far latch opens a permanent low return and permits anyone to exit.
  size => {
    if (size === 2) return {
      hint: 'Stand on a friend, settle, then jump onto the high bough. Its plate unfolds steps for everyone.',
      platforms: [
        ground('orchard-floor', 0, 1200),
        ledge('high-bough', 552, 445, 176, 18),
        { ...ledge('unfolded-step', 432, 495, 112), switchIds: ['bough'] },
        { ...ledge('bough-walk', 728, 445, 296), kind: 'bridge', switchIds: ['bough'] },
        ground('upper-hearth', 1024, 176, 445),
        lintel('bough-door', 944, 300),
      ],
      switches: [plate('bough', 'Unfold', 568, 1, 'latch', 445, 128)],
      gates: [{ id: 'bough-door', x: 944, y: 300, w: 24, h: 255, switchIds: ['bough'], requireKey: true }],
      hazards: [],
      keys: [{ id: 'bough-ember', x: 680, y: 374, w: 24, h: 24 }],
      crates: [],
      exit: { x: 1040, y: 333, w: 152, h: 112 },
      signs: [
        { x: 462, y: 516, text: 'A shoulder, then a hop', icon: 'stack' },
        { x: 574, y: 410, text: 'Unfold the way', icon: 'switch' },
        { x: 1044, y: 306, text: 'Regroup up here', icon: 'home' },
      ],
    };
    const helpers = size - 2;
    const near = Math.ceil(helpers / 2);
    const far = Math.floor(helpers / 2);
    const crossing = ['bough', 'boost', 'near', ...(far ? ['far'] : [])];
    return {
      hint: far
        ? 'Boost a scout. Keep B lit and split the crew across C and D. The far plate opens a low way home for everyone.'
        : 'Boost a scout. Keep B lit while a friend holds C. The far plate opens a low way home for everyone.',
      platforms: [
        ground('orchard-floor', 0, 1200),
        ledge('high-bough', 552, 445, 176, 18),
        { ...ledge('bough-near-walk', 728, 445, 96), kind: 'bridge', switchIds: ['bough', 'boost', 'near'] },
        { ...ledge('bough-far-walk', 824, 445, 120), kind: 'bridge', switchIds: ['bough', 'boost', far ? 'far' : 'near'] },
        // The temporary span ends BEFORE this rung: it cannot reappear as a
        // ceiling over a fallen scout. Both sides of the rung remain recoverable.
        // 40px wide: no stance on the rung can sit under the perch face, so a
        // natural right+jump from anywhere on it reaches the perch (C2-R5 bonk).
        ledge('home-rung', 944, 505, 40, 6),
        ledge('home-perch', 992, 445, 40),
        lintel('bough-door', 920, 300),
        lintel('bough-exit-door', 1032),
      ],
      switches: [
        plate('bough', 'Wake the bough', 568, 1, 'latch', 445, 128),
        plate('boost', 'Anchor', 484, 1, 'hold', FLOOR, 68, 0.25),
        { ...plate('near', 'Near span', 584, near, 'hold', FLOOR, near * 40 + 16, 0.25),
          teamWeight: { group: 'bough-crew', reserve: 2, priority: 0 } },
        ...(far ? [{ ...plate('far', 'Far span', 752, far, 'hold', FLOOR, far * 40 + 16, 0.25),
          teamWeight: { group: 'bough-crew', reserve: 2, priority: 1 } }] : []),
        plate('home', 'Low way home', 992, 1, 'latch', 445, 40),
      ],
      gates: [
        { id: 'bough-door', x: 920, y: 300, w: 24, h: 145, switchIds: crossing, requireKey: true },
        { id: 'bough-return-door', x: 920, y: 445, w: 24, h: 110, switchIds: ['home'] },
        { id: 'bough-exit-door', x: 1032, y: 335, w: 24, h: 220, switchIds: ['home'] },
      ],
      hazards: [],
      keys: [{ id: 'bough-ember', x: 680, y: 374, w: 24, h: 24 }],
      crates: [],
      exit: { x: 1064, y: 443, w: 128, h: 112 },
      signs: [
        { x: 462, y: 516, text: 'Boost · hold B', icon: 'stack' },
        { x: 578, y: 406, text: 'A wakes the branches', icon: 'switch' },
        { x: 744, y: 503, text: far ? 'C + D hold the walk' : 'C holds the walk', icon: 'hold' },
        { x: 982, y: 405, text: 'Land: open the low way', icon: 'switch' },
      ],
    };
  },
  // 3: The scout latches the upper return route AND the exit cut. A buffered
  // landing may skip the plate, but cannot strand a keeper by auto-entering.
  size => ({
    hint: 'Keep the lower light pressed while a scout crosses. Land on B to open the high way and the way home.',
    platforms: [
      ground('relay-floor', 0, 1200),
      lintel('relay-wall', 824, 285, 28),
      ledge('return-step-1', 596, 495, 64),
      ledge('return-step-2', 676, 435, 64),
      ledge('return-step-3', 756, 375, 68),
      { ...ledge('return-walk', 824, 375, 160), kind: 'bridge', switchIds: ['return'] },
      lintel('return-exit-door', 1016),
    ],
    switches: [
      { ...plate('keeper', 'Keep lit', 400, keepers(size), 'hold', FLOOR, keepers(size) * 44 + 24, 0.3),
        teamWeight: { group: 'relay-keepers', reserve: 1, priority: 0 } },
      plate('return', 'Way back', 900, 1, 'latch', FLOOR, 96),
    ],
    gates: [
      { id: 'lower-door', x: 824, y: 435, w: 28, h: 120, switchIds: ['keeper'] },
      { id: 'upper-door', x: 824, y: 285, w: 28, h: 150, switchIds: ['return'] },
      { id: 'return-exit-door', x: 1016, y: 335, w: 24, h: 220, switchIds: ['return'] },
    ],
    hazards: [],
    keys: [{ id: 'relay-ember', x: 940, y: 513, w: 24, h: 24 }],
    crates: [],
    exit: { x: 1044, y: 443, w: 148, h: 112 },
    signs: [
      { x: 402, y: 502, text: 'Hold A for the scout', icon: 'hold' },
      { x: 600, y: 460, text: 'Keepers return above', icon: 'jump' },
      { x: 900, y: 502, text: 'Land on B · both ways', icon: 'switch' },
    ],
  }),
  // 4: Half the party HOLDS the middle stone up while the other half crosses it
  // and gathers on the far signal, which makes the broad lower bridge permanent
  // for the holders. The low first stone (20 px up, entirely on the bank's lip)
  // cannot reach the far bank alone even with a 31 px body overhang plus a full
  // coyote-time edge jump (longest reach 868 < 880). A two-body boost from the lip
  // CAN cross without holders: that alternative is deliberate cooperation too.
  // Spawns sit right of the hold plate: holders walk left, scouts walk right,
  // nobody passes anyone on the ground.
  size => {
    const holders = keepers(size);
    const scouts = size - holders;
    // Holders stand in two-high columns: a plate too narrow for the quota side by side.
    const holdWidth = 48 * Math.ceil(holders / 2) - 8;
    const signalWidth = scouts * 36 + 16;
    return {
      hint: 'Half of you hold the near plate so the middle stone rises. The others hop the stones and gather on the far signal, which bridges the brook for the holders.',
      platforms: [
        ground('near-bank', 0, 640), ground('far-bank', 880, 320),
        ledge('skip-1', 600, 535, 40, 12),
        { ...ledge('skip-2', 684, 455, 64), kind: 'bridge', switchIds: ['bank'] },
        { ...ledge('brook-span', 640, FLOOR, 240), kind: 'bridge', switchIds: ['span'] },
        lintel('signal-door', 1040),
      ],
      switches: [
        { ...plate('bank', 'Raise the stone', 280 - holdWidth, holders, 'hold', FLOOR, holdWidth, 0.3),
          teamWeight: { group: 'skip-crew', reserve: 0, priority: 0 } },
        { ...plate('span', 'Bridge home', 1040 - signalWidth, scouts, 'latch', FLOOR, signalWidth),
          teamWeight: { group: 'skip-crew', reserve: 0, priority: 1 } },
      ],
      gates: [{ id: 'signal-door', x: 1040, y: 335, w: 24, h: 220, switchIds: ['span'], requireKey: true }],
      hazards: [{ id: 'brook', x: 640, y: 605, w: 240, h: 70, kind: 'water' }],
      keys: [{ id: 'skipstone-ember', x: 712, y: 409, w: 24, h: 24 }],
      crates: [],
      exit: { x: 1072, y: 443, w: 128, h: 112 },
      signs: [
        { x: 180, y: 502, text: holders > 1 ? `${holders} hold · stack in pairs` : 'Hold · the stone rises', icon: 'hold' },
        { x: 716, y: 416, text: 'Cross while held', icon: 'jump' },
        { x: 960, y: 502, text: `${scouts} on B · bridge home`, icon: 'switch' },
      ],
    };
  },
  // 5: Same-height banks, a slow deck, repeatable trips. The ferry's positive
  // excursion docks at the far bank (400+108+184 = 692). The whole connected
  // party must stand ashore on the mooring strip before the door opens.
  // Controllers observe getPlatforms rather than assuming a cycle phase.
  size => ({
    hint: 'Board when the ferry is close. Ride toward the far bank, hop ashore, and leave room for the next friends.',
    platforms: [
      ground('west-jetty', 0, 400), ground('east-jetty', 692, 508),
      { ...ledge('moonwake', 400, FLOOR, 184, 18), motion: { axis: 'x', distance: 108, period: 14, phase: 0 } },
      lintel('moor-door', 1032),
    ],
    // A 36px landing apron (692..728) precedes the mooring strip (728..1032, one slot
    // per courier), so the last arrival never lands on a parked friend.
    switches: [gather('moor', 'Every light ashore', 1032, size)],
    gates: [{ id: 'moor-door', x: 1032, y: 335, w: 24, h: 220, switchIds: ['moor'], requireKey: true }],
    hazards: [{ id: 'deep-brook', x: 400, y: 605, w: 292, h: 70, kind: 'water' }],
    keys: [{ id: 'moonwake-ember', x: 800, y: 509, w: 24, h: 24 }],
    crates: [],
    exit: { x: 1064, y: 443, w: 128, h: 112 },
    signs: [
      { x: 280, y: 492, text: 'Wait for the ferry', icon: 'wait' },
      { x: 700, y: 468, text: 'Hop ashore; make room', icon: 'jump' },
      { x: 860, y: 501, text: 'EVERY LIGHT ashore', icon: 'friends' },
    ],
  }),
  // 6: A 180 px depot wall keeps the parcel out of reach of a lone shoulder AND of a
  // three-high tower (whose top reaches 383, below the 375 wall top). The
  // crowd HOLDS the lift plate so a lift ledge exists beside the wall: base's
  // shoulder → lift → wall top → curb. Parking the parcel opens the far door AND
  // makes a permanent return (step + ledge) so the base and the crowd can follow.
  // With two couriers the lift ledge is permanent; the lift quota shrinks with the
  // connected roster (group reserve 2), so an 8→2 party keeps a base and a pusher.
  size => {
    const lifters = size - 2;
    // Lifters stand in two-high columns on a plate too narrow for the quota side by side.
    const liftWidth = 48 * Math.ceil(lifters / 2) - 8;
    const liftRect = { x: 568, y: 445, w: 72, h: 16 };
    return {
      hint: 'Hold the lift plate so a ledge appears beside the depot wall; a friend boosts the pusher onto it. Park the parcel: its weight opens the far door and unfolds the way back for everyone.',
      platforms: [
        ground('depot-floor', 0, 1200), lintel('cargo-door', 1016),
        { id: 'depot-wall', x: 648, y: 375, w: 24, h: 180, kind: 'stone' },
        lifters > 0
          ? { id: 'depot-lift', ...liftRect, kind: 'bridge' as const, switchIds: ['lift'] }
          : { id: 'depot-lift', ...liftRect, kind: 'wood' as const },
        { id: 'depot-return', ...liftRect, kind: 'bridge' as const, switchIds: ['cargo'] },
        { ...ledge('depot-step', 520, 491, 48), switchIds: ['cargo'] },
        ground('cargo-left-curb', 684, 24, 539), ground('cargo-stop', 900, 32, 515),
      ],
      switches: [
        ...(lifters > 0 ? [{
          ...plate('lift', 'Raise the lift', 416 - liftWidth, lifters, 'hold', FLOOR, liftWidth, 0.3),
          teamWeight: { group: 'depot-lift', reserve: 2, priority: 0 },
        }] : []),
        plate('cargo', 'Parcel rests', 796, 2, 'hold', FLOOR, 104, 0.25),
      ],
      gates: [{ id: 'cargo-door', x: 1016, y: 335, w: 24, h: 220, switchIds: ['cargo'], requireKey: true }],
      hazards: [],
      keys: [{ id: 'cargo-ember', x: 960, y: 509, w: 24, h: 24 }],
      crates: [{ id: 'parcel', x: 724, y: 507, w: 48, h: 48, weight: 2 }],
      exit: { x: 1052, y: 443, w: 140, h: 112 },
      signs: [
        ...(lifters > 0 ? [{ x: 290, y: 502, text: lifters > 1 ? `${lifters} hold · stack in pairs` : 'Hold · the lift rises', icon: 'hold' }] : []),
        { x: 470, y: 456, text: 'Shoulder, lift, wall', icon: 'stack' },
        { x: 782, y: 484, text: 'Parcel → stop', icon: 'crate' },
        { x: 934, y: 470, text: 'Parked: way back unfolds', icon: 'switch' },
      ],
    };
  },
  // 7: Shelter is needed for the first crossing. The far key enables the existing
  // 2.6-second dark intervals, freeing the keepers without an exited teammate.
  size => ({
    hint: 'Shelter first: hold A for the first crossing. The far key wakes quiet intervals; keepers can then cross in the dark.',
    platforms: [ground('hushlight-path', 0, 1200), lintel('quiet-door', 1032)],
    switches: [
      { ...plate('shade', 'Shelter', 400, keepers(size), 'hold', FLOOR, keepers(size) * 44 + 24, 0.35),
        teamWeight: { group: 'shade-keepers', reserve: 1, priority: 0 } },
      plate('quiet', 'Meet again', 820, quorum(size)),
    ],
    gates: [{ id: 'quiet-door', x: 1032, y: 335, w: 24, h: 220, switchIds: ['quiet'], requireKey: true }],
    hazards: [{
      id: 'hush-beam', x: 624, y: 375, w: 180, h: 180, kind: 'beam',
      cycle: { on: 4, period: 6.6, phase: 0 }, cycleAfterKey: true, disabledBy: ['shade'],
    }],
    keys: [{ id: 'hushlight-ember', x: 856, y: 509, w: 24, h: 24 }],
    crates: [],
    exit: { x: 1064, y: 443, w: 128, h: 112 },
    signs: [
      { x: 402, y: 502, text: 'Shelter first · hold A', icon: 'hold' },
      { x: 544, y: 464, text: 'Key wakes quiet', icon: 'wait' },
      { x: 824, y: 477, text: 'Meet again', icon: 'friends' },
    ],
  }),
  // 8: A genuinely tall tower opens the ember door. The tower plate is one body
  // wide, so only stacked weight counts: two bodies for a pair or trio, three for
  // four or five, four for six, FIVE for seven or eight (a stool friend beside the
  // tower — two stool friends for five — lets climbers reach the upper heads). The latch opens a sealed door beside the tower;
  // the ember waits in the alcove behind it. Then the whole team forms a row of
  // two-high towers on the council (one column per pair), and cargo remains a
  // weight-two handoff. The tower quota is restated by connected count, so a shrunken
  // party still opens the door with the tower its numbers allow (a pair still stacks).
  size => {
    const tower = size <= 3 ? 2 : size <= 5 ? 3 : size <= 6 ? 4 : 5;
    return {
      hint: tower === 2
        ? 'Stand on the tower plate two high to open the ember door. Gather everyone on the council in pairs, then park the parcel to light the crossing.'
        : `Build a tower ${tower} high on the tower plate (friends beside it are the steps) to open the ember door. Gather everyone on the council in pairs, then park the parcel to light the crossing.`,
      platforms: [
        ground('council-bank', 0, 784), ground('last-hearth', 1024, 176),
        lintel('ember-door', 492), lintel('council-door', 556),
        ground('last-left-curb', 584, 16, 539), ground('last-stop', 768, 16, 539),
        { ...ledge('last-span', 784, FLOOR, 240), kind: 'bridge', switchIds: ['council', 'cargo'] },
      ],
      switches: [
        // The tower rule restated by connected count: 2 for up to three, 3 for four or five, 4 for six, 5 for seven or eight.
        { ...plate('tower', 'Tower', 424, tower, 'latch', FLOOR, 40), byConnected: [0, 1, 2, 2, 3, 3, 4, 5, 5] },
        plate('council', 'Every light', 548 - (48 * Math.ceil(size / 2) - 8), size, 'latch', FLOOR, 48 * Math.ceil(size / 2) - 8),
        plate('cargo', 'Carry the light', 688, 2, 'hold', FLOOR, 80, 0.4),
      ],
      gates: [
        { id: 'ember-door', x: 492, y: 335, w: 24, h: 220, switchIds: ['tower'] },
        { id: 'council-door', x: 556, y: 335, w: 24, h: 220, switchIds: ['council'], requireKey: true },
      ],
      hazards: [
        { id: 'last-water', x: 784, y: 605, w: 240, h: 70, kind: 'water' },
        { id: 'last-beam', x: 800, y: 355, w: 208, h: 200, kind: 'beam', cycle: { on: 3.5, period: 6, phase: 1 }, disabledBy: ['cargo'] },
      ],
      keys: [{ id: 'hearth-ember', x: 528, y: 513, w: 24, h: 24 }],
      crates: [{ id: 'last-parcel', x: 612, y: 507, w: 48, h: 48, weight: 2 }],
      exit: { x: 1056, y: 443, w: 136, h: 112 },
      signs: [
        { x: 444, y: 340, text: `Tower of ${tower} · opens the door`, icon: 'stack' },
        { x: 470, y: 485, text: size > 2 ? 'EVERY LIGHT · stack in pairs' : 'EVERY LIGHT COUNTS', icon: 'friends' },
        { x: 670, y: 476, text: 'Park the last light', icon: 'crate' },
        { x: 1056, y: 408, text: 'All aglow', icon: 'home' },
      ],
    };
  },
];

export function getLevel(index: number, partySize: number): LevelDef {
  if (!Number.isInteger(index) || index < 0 || index >= LEVEL_COUNT) {
    throw new RangeError(`Level index must be an integer from 0 to ${LEVEL_COUNT - 1}`);
  }
  if (!Number.isFinite(partySize)) throw new RangeError('Party size must be finite');
  const size = Math.max(2, Math.min(8, Math.trunc(partySize)));
  return {
    ...LEVEL_META[index],
    spawn: index === 7 ? spawns(size, 40, 40) : index === 3 ? spawns(size, 288, 40) : spawns(size),
    ...layouts[index](size),
  };
}
